import React from 'react';
import dynamic from 'next/dynamic';
import get from 'lodash/get';
import getConfig from 'next/config';

import Element from '~/src/components/element/Element';
import TickerEvent from '~/src/components/ticker/event/TickerEvent';
import Scoreboard from '~/src/components/ticker/scoreboard/Scoreboard';
import ScoreboardTennis from '~/src/components/ticker/scoreboard/ScoreboardTennis';
import ToggleButton from '~/src/components/toggleButton/ToggleButton';
import ActivityBar from '~/src/components/activity/bar/ActivityBar';
import TickerDate from '~/src/components/ticker/date/TickerDate';
import Rating from '~/src/components/rating/Rating';
import StructuredArticle from '~/src/components/structured/article/StructuredArticle';

import {getContentUrl} from '~/src/utilities/helpers/link';
import TypeContext from '~/src/components/context/TypeContext';
import {withSettingsContext} from '~/src/components/context/SettingsContext';
import {getJsonRequest} from '~/src/utilities/helpers/request';
import {ActionBubbleFormat} from '~/src/utilities/constants/formats';
import {utcToFormat} from '~/src/utilities/helpers/date';
import {isLegacyAppSession} from '~/src/utilities/helpers/client';
import initPolyglot from '~/src/utilities/libs/polyglot';
import rollbar from '~/src/utilities/libs/rollbar';
import {onRating} from '~/src/utilities/helpers/rating';

import css from './TickerContent.module.scss';
import i18nStrings from './TickerContent.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const ActionBubble = dynamic({
    loader: () => import('~/src/components/actionBubble/ActionBubble'),
    loading: () => false,
    ssr: false
});

const {publicRuntimeConfig: config} = getConfig();

class TickerContent extends React.Component {
    filterOutUpdatedEvents(events) {
        return [
            ...this.state.events.filter((event) => {
                return events.findIndex((candidate) => candidate.id === event.id) === -1;
            })
        ];
    }

    insertNewEvent(events, newEvent) {
        const index = events.findIndex((element) => {
            return element.id === newEvent.showAfter;
        });
        if (index !== -1) {
            events.splice(index + 1, 0, newEvent);
        }
    }

    updateView(data, isUpdate) {
        if (data.content) {
            const newEvents = data.content.events || [];
            const newTicker = data.content.ticker || {};
            const ticker = {...this.state.ticker, ...newTicker};
            const nextpage = !isUpdate || data.content.nextpage ? data.content.nextpage : this.state.nextpage;
            const update = data.content.update || this.state.update;
            const polling = data.content.polling || this.state.polling;
            const events = this.filterOutUpdatedEvents(newEvents);

            newEvents.forEach((newEvent) => {
                if (newEvent.showAfter) {
                    this.insertNewEvent(events, newEvent);
                } else {
                    events.unshift(newEvent);
                }
            });

            this.setState({
                ticker,
                events,
                nextpage,
                update,
                polling
            });
        }
    }

    async fetchFeed(feed, isUpdate) {
        try {
            const data = await getJsonRequest(feed);
            this.updateView(data, isUpdate);
        } catch (error) {
            rollbar.warning(`[TickerContent] Error while loading ticker content feed: ${error.message}`, error);
        }
    }

    loopFetchFeed = (initialLoad) => {
        if (!initialLoad) {
            this.fetchFeed(this.state.update, true);
        }
        this.timeout = setTimeout(this.loopFetchFeed, this.state.polling);
    };

    hasUpdateFeed() {
        return this.state.polling && this.state.update;
    }

    webSocketIsAllowed() {
        // FEATURE FLAG: WebSocket is not working yet, temporary disabled
        // return window.WebSocket && this.props.content && this.props.content.wsUpdate;
        return false;
    }

    webSocketIsRunning() {
        return this.socket && this.socket.readyState === 1;
    }

    openWebSocket() {
        this.socket = new WebSocket(this.props.content.wsUpdate);
        this.socket.addEventListener('message', (event) => {
            this.updateView(event.data, true);
        });
    }

    openConnection(initialLoad) {
        if (this.webSocketIsAllowed()) {
            this.openWebSocket();
        } else if (this.hasUpdateFeed()) {
            this.loopFetchFeed(initialLoad);
        }
    }

    closeConnection() {
        if (this.webSocketIsRunning()) {
            this.socket.close();
        } else if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }

    onToggleOrder = () => {
        this.setState({
            isReversed: !this.state.isReversed
        });
    };

    onToggleAutoUpdate = () => {
        this.setState({
            autoUpdate: !this.state.autoUpdate
        });
    };

    getUpdatedSettings = (settings) => {
        if (settings) {
            return {
                ...settings,
                sharingEnabled: !(this.props.context.settings.isWebView || this.state.isLegacyApp) && settings.sharingEnabled
            };
        }
    };

    prepareForLegacyApp() {
        const isLegacyApp = !this.props.context.settings.isWebView && isLegacyAppSession();
        this.setState({isLegacyApp});
    }

    onRatingAction = onRating({
        id: this.props.id,
        authors: this.props.content.meta.authors,
        fullUrlPath: this.props.content.meta.mainCategoryFullUrlPath,
        type: this.props.type
    });

    state = {
        ticker: this.props.content.ticker,
        events: this.props.content.events,
        nextpage: this.props.content.nextpage,
        update: this.props.content.update,
        polling: this.props.content.polling,
        autoUpdate: true,
        isReversed: false,
        isLegacyApp: false
    };

    componentDidMount() {
        this.openConnection(true);
        this.prepareForLegacyApp();
    }

    componentDidUpdate() {
        if (this.state.autoUpdate) {
            if (!this.webSocketIsRunning() && !this.timeout) {
                this.openConnection();
            }
        } else {
            this.closeConnection();
        }
    }

    componentWillUnmount() {
        this.closeConnection();
    }

    render() {
        const {content, teasers} = this.props;
        const {ticker, events, nextpage: nextPageFeed, isReversed, autoUpdate} = this.state;

        const sortedEvents = isReversed ? [...events].reverse() : events;

        const canonicalUrl = new URL(getContentUrl(content.meta.url), config.canonicalBaseUrl).href;

        const type = content.type;
        const {titleHeader, title} = teasers.web;
        const headline = titleHeader ? `${titleHeader}: ${title}` : title;

        const datePublished = get(content, 'meta.published', content.created);
        const dateModified = get(content, 'meta.updated', content.updated);

        let lastDateString = '';

        return (
            <TypeContext value={this.props.type}>
                <StructuredArticle
                    datePublished={datePublished}
                    dateModified={dateModified}
                    authors={content.meta.authors}
                    headline={headline}
                    description={teasers.web.description}
                    images={teasers.web.images}
                    canonicalUrl={canonicalUrl}
                />

                <article className={css.ticker}>
                    <section className={css.tickerHeader}>
                        {ticker.title && (
                            <h1 className={css.tickerTitle}>
                                {ticker.titleHeader && (
                                    <span className={css.titleHeader}>
                                        {ticker.titleHeader}
                                        <span className="visually-hidden" aria-hidden="true" role="presentation">
                                            :{' '}
                                        </span>
                                    </span>
                                )}
                                {ticker.title}
                            </h1>
                        )}
                        {ticker.lead && (
                            <div className={css.tickerLead}>
                                {ticker.lead.elements.map((element, index) => (
                                    <Element key={'lead_' + index} {...element} />
                                ))}
                            </div>
                        )}
                    </section>

                    {ticker.data && (
                        <section className={css.scoreboardWrapper}>
                            {type === 'tennis' ? <ScoreboardTennis {...ticker.data} type={type} /> : <Scoreboard {...ticker.data} type={type} />}
                        </section>
                    )}

                    {ticker.intro && (
                        <section className={css.tickerIntro}>
                            {ticker.intro.elements.map((element, index) => (
                                <Element key={'intro_' + index} {...element} />
                            ))}
                        </section>
                    )}

                    {get(content, 'meta.settings.likesEnabled', true) && <Rating metadataId={this.props.id} onRating={this.onRatingAction} />}

                    <ActivityBar
                        data={get(this.props, 'content.meta.clientdata.community')}
                        settings={this.getUpdatedSettings(get(this.props, 'content.meta.settings'))}
                        metadataId={this.props.id}
                        openCommentPage={true}
                    />

                    <section className={css.tickerInformation}>
                        <div className={css.tickerStatus}></div>
                    </section>

                    <section className={css.tickerButtons}>
                        <div className={css.tickerButton}>
                            <ToggleButton checked={!isReversed} label={getTranslation('order')} callback={this.onToggleOrder} variant="order" />
                        </div>
                        <div className={css.tickerButton}>
                            <ToggleButton checked={autoUpdate} label={getTranslation('autoUpdate')} callback={this.onToggleAutoUpdate} />
                        </div>
                    </section>

                    {isReversed && nextPageFeed && (
                        <section className={css.loadMoreSection}>
                            <button className={css.loadMoreEvents} onClick={() => this.fetchFeed(nextPageFeed, false)}>
                                {getTranslation('loadMore')}
                            </button>
                        </section>
                    )}

                    <section className={css.tickerContent}>
                        {sortedEvents.map((event, index) => {
                            const fragments = [
                                <TickerEvent
                                    key={event.id}
                                    {...event}
                                    tickerType={type}
                                    meta={get(this.props, 'content.meta', {})}
                                    metadataId={this.props.id}
                                />
                            ];
                            if (get(event, 'data.dateTime')) {
                                const dateString = utcToFormat(event.data.dateTime, 'dddd, DD.MM.YYYY');

                                if (lastDateString !== dateString) {
                                    lastDateString = dateString;

                                    fragments.unshift(<TickerDate key={dateString} dateString={dateString} />);
                                }
                            }

                            return fragments;
                        })}
                    </section>

                    {!isReversed && nextPageFeed && (
                        <section className={css.loadMoreSection}>
                            <button className={css.loadMoreEvents} onClick={() => this.fetchFeed(nextPageFeed, false)}>
                                {getTranslation('loadMore')}
                            </button>
                        </section>
                    )}
                </article>

                <div className={css.actionBubbleContainer}>
                    <div className={css.actionBubble}>
                        <ActionBubble
                            metadataId={this.props.id}
                            format={ActionBubbleFormat.ACTION_BUBBLE_ARTICLE}
                            settings={this.getUpdatedSettings(get(this.props, 'content.meta.settings'))}
                            openCommentPage={true}
                        />
                    </div>
                </div>
            </TypeContext>
        );
    }
}

export default withSettingsContext(TickerContent);
